import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Plus, Upload, Trash2, History, Download, Tag, Printer } from "lucide-react";
import { AuditLogDialog } from "../components/AuditLogDialog";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";

// Componente para mostrar localizações de um produto
function ProductLocations({ cod }: { cod: string }) {
  const { data: locations = [], isLoading } = trpc.products.locations.useQuery({ cod });
  
  if (isLoading) return <span className="text-muted-foreground text-xs">Carregando...</span>;
  if (locations.length === 0) return <span className="text-muted-foreground text-xs">-</span>;
  
  return (
    <div className="flex flex-wrap gap-1">
      {locations.map((loc) => (
        <span key={loc} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
          {loc}
        </span>
      ))}
    </div>
  );
}

function ProductExternalLocations({ cod }: { cod: string }) {
  const { data: locations = [], isLoading } = trpc.products.externalLocations.useQuery({ cod });
  
  if (isLoading) return <span className="text-muted-foreground text-xs">Carregando...</span>;
  if (locations.length === 0) return <span className="text-muted-foreground text-xs">-</span>;
  
  return (
    <div className="flex flex-wrap gap-1">
      {locations.map((loc) => (
        <span key={loc} className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
          {loc}
        </span>
      ))}
    </div>
  );
}

export default function SomenteConsultaProdutos() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useGoogleAuth();
  const [showNewForm, setShowNewForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  
  // Estados do formulário
  const [cod, setCod] = useState("");
  const [descricao, setDescricao] = useState("");
  const [qtdCaixaMaster, setQtdCaixaMaster] = useState("");
  const [altura, setAltura] = useState("");
  const [largura, setLargura] = useState("");
  const [comprimento, setComprimento] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [emitenteId, setEmitenteId] = useState<number | null>(null);
  const [ncm, setNcm] = useState("");
  const [eanUnit, setEanUnit] = useState("");
  const [eanCaixa, setEanCaixa] = useState("");
  const [unidadeFiscal, setUnidadeFiscal] = useState("");
  const [unidadeComercial, setUnidadeComercial] = useState("");
  const [alturaUnitaria, setAlturaUnitaria] = useState("");
  const [larguraUnitaria, setLarguraUnitaria] = useState("");
  const [comprimentoUnitario, setComprimentoUnitario] = useState("");
  const [custo, setCusto] = useState("");
  const [venda, setVenda] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [custoEditMode, setCustoEditMode] = useState(false);
  

  // Queries
  const { data: products = [], refetch } = trpc.products.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  const { data: auditLogs = [], isLoading: auditLoading } = trpc.products.auditLog.useQuery(
    { productId: selectedProductId! },
    { enabled: !!selectedProductId && auditDialogOpen }
  );

  // Mutations
  const createMutation = trpc.products.create.useMutation();
  const createBulkMutation = trpc.products.createBulk.useMutation();
  const deleteMutation = trpc.products.delete.useMutation();
  const updateMutation = trpc.products.update.useMutation({
    onSuccess: () => {
      toast.success("Produto atualizado com sucesso");
      setEditingId(null);
      setEditValues({});
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar produto");
    },
  });

  // Resetar para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Loading check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Apenas líderes e admins podem acessar

  // Filtrar produtos por busca
  const filteredProducts = products.filter((product) => {
    const search = searchTerm.toLowerCase();
    return (
      product.cod.toLowerCase().includes(search) ||
      (product.descricao && product.descricao.toLowerCase().includes(search))
    );
  });

  // Calcular paginação
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  
  const handleStartEdit = (product: any) => {
    setEditingId(product.id);
    setCod(product.cod || "");
    setDescricao(product.descricao || "");
    setQtdCaixaMaster(product.qtdCaixaMaster?.toString() || "");
    setAltura(product.altura?.toString() || "");
    setLargura(product.largura?.toString() || "");
    setComprimento(product.comprimento?.toString() || "");
    setQuantidade(product.quantidade?.toString() || "");
    setEmitenteId(product.emitenteId || null);
    setNcm(product.ncm || "");
    setEanUnit(product.eanUnit || "");
    setEanCaixa(product.eanCaixa || "");
    setUnidadeFiscal(product.unidadeFiscal || "");
    setUnidadeComercial(product.unidadeComercial || "");
    setAlturaUnitaria(product.alturaUnitaria?.toString() || "");
    setLarguraUnitaria(product.larguraUnitaria?.toString() || "");
    setComprimentoUnitario(product.comprimentoUnitario?.toString() || "");
    setCusto(product.custo?.toString() || "");
    setVenda(product.venda?.toString() || "");
    setDialogMode("edit");
    setIsDialogOpen(true);
  };
  
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };
  
  const handleSaveEdit = async (id: number) => {
    await updateMutation.mutateAsync({
      id,
      descricao: editValues.descricao,
      qtdCaixaMaster: Number(editValues.qtdCaixaMaster),
      altura: Number(editValues.altura),
      largura: Number(editValues.largura),
      comprimento: Number(editValues.comprimento),
      emitenteId: editValues.emitenteId || null,
    });
  };
  
  const cbm = altura && largura && comprimento
    ? ((Number(altura) * Number(largura) * Number(comprimento)) / 1000000).toFixed(6)
    : "0.000000";
  
  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      // Limpar todos os campos quando fechar o modal
      setCod("");
      setDescricao("");
      setQtdCaixaMaster("");
      setAltura("");
      setLargura("");
      setComprimento("");
      setQuantidade("");
      setEmitenteId(null);
      setNcm("");
      setEanUnit("");
      setEanCaixa("");
      setUnidadeFiscal("");
      setUnidadeComercial("");
      setAlturaUnitaria("");
      setLarguraUnitaria("");
      setComprimentoUnitario("");
      setCusto("");
      setVenda("");
      setEditingId(null);
      setDialogMode("create");
      setCustoEditMode(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (dialogMode === "edit" && editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          cod,
          descricao: descricao || undefined,
          qtdCaixaMaster: qtdCaixaMaster ? Number(qtdCaixaMaster) : undefined,
          altura: altura ? Number(altura) : undefined,
          largura: largura ? Number(largura) : undefined,
          comprimento: comprimento ? Number(comprimento) : undefined,
          quantidade: quantidade ? Number(quantidade) : undefined,
          emitenteId: emitenteId || undefined,
          ncm: ncm || undefined,
          eanUnit: eanUnit || undefined,
          eanCaixa: eanCaixa || undefined,
          unidadeFiscal: unidadeFiscal || undefined,
          unidadeComercial: unidadeComercial || undefined,
          alturaUnitaria: alturaUnitaria ? Number(alturaUnitaria) : undefined,
          larguraUnitaria: larguraUnitaria ? Number(larguraUnitaria) : undefined,
          comprimentoUnitario: comprimentoUnitario ? Number(comprimentoUnitario) : undefined,
          custo: custo ? Number(custo) : undefined,
          venda: venda ? Number(venda) : undefined,
        });
        toast.success("Produto atualizado com sucesso!");
      } else {
        await createMutation.mutateAsync({
          cod,
          descricao: descricao || undefined,
          qtdCaixaMaster: qtdCaixaMaster ? Number(qtdCaixaMaster) : undefined,
          altura: altura ? Number(altura) : undefined,
          largura: largura ? Number(largura) : undefined,
          comprimento: comprimento ? Number(comprimento) : undefined,
          quantidade: quantidade ? Number(quantidade) : undefined,
          emitenteId: emitenteId || undefined,
          ncm: ncm || undefined,
          eanUnit: eanUnit || undefined,
          eanCaixa: eanCaixa || undefined,
          unidadeFiscal: unidadeFiscal || undefined,
          unidadeComercial: unidadeComercial || undefined,
          alturaUnitaria: alturaUnitaria ? Number(alturaUnitaria) : undefined,
          larguraUnitaria: larguraUnitaria ? Number(larguraUnitaria) : undefined,
          comprimentoUnitario: comprimentoUnitario ? Number(comprimentoUnitario) : undefined,
          custo: custo ? Number(custo) : undefined,
          venda: venda ? Number(venda) : undefined,
        });
        toast.success("Produto cadastrado com sucesso!");
      }
      
      // Limpar formulário
      setCod("");
      setDescricao("");
      setQtdCaixaMaster("");
      setAltura("");
      setLargura("");
      setComprimento("");
      setQuantidade("");
      setEmitenteId(null);
      setNcm("");
      setEanUnit("");
      setEanCaixa("");
      setUnidadeFiscal("");
      setUnidadeComercial("");
      setAlturaUnitaria("");
      setLarguraUnitaria("");
      setComprimentoUnitario("");
      setCusto("");
      setVenda("");
      setEditingId(null);
      setIsDialogOpen(false);
      
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao cadastrar produto");
    }
  };
  
  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza que deseja excluir este produto?")) return;
    
    try {
      await deleteMutation.mutateAsync({ id });
      toast.success("Produto excluído com sucesso!");
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir produto");
    }
  };
  
  const handleExportExcel = () => {
    try {
      // Preparar dados para exportação (usar dados filtrados)
      const dataToExport = filteredProducts.map((product) => ({
        "COD": product.cod,
        "DESCRIÇÃO": product.descricao || "",
        "NCM": product.ncm || "",
        "UNIDADE COMERCIAL": product.unidadeComercial || "",
        "QTD CAIXA MASTER": product.qtdCaixaMaster || "",
        "ALTURA UNITÁRIA (CM)": product.alturaUnitaria || "",
        "LARGURA UNITÁRIA (CM)": product.larguraUnitaria || "",
        "COMPRIMENTO UNITÁRIO (CM)": product.comprimentoUnitario || "",
        "UNIDADE FISCAL": product.unidadeFiscal || "",
        "ALTURA CAIXA (CM)": product.altura || "",
        "LARGURA CAIXA (CM)": product.largura || "",
        "COMPRIMENTO CAIXA (CM)": product.comprimento || "",
        "CUSTO (R$)": product.custo ? Number(product.custo).toFixed(2) : "",
        "VENDA (R$)": product.venda ? Number(product.venda).toFixed(2) : "",
        "EAN UNITÁRIO": product.eanUnit || "",
        "EAN CAIXA": product.eanCaixa || "",
        "EMITENTE ID": product.emitenteId || "",
      }));

      // Criar workbook e worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Produtos");

      // Gerar arquivo Excel
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `produtos_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Produtos exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar produtos");
    }
  };
  
  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const XLSX = await import("xlsx");
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      // Mapear colunas do Excel para formato esperado
      const productsToImport = jsonData.map((row: any) => ({
        cod: String(row["COD"] || row["Código"] || row["cod"] || ""),
        descricao: row["DESCRIÇÃO"] || row["Descrição"] || row["descricao"] || undefined,
        ncm: row["NCM"] || row["ncm"] || undefined,
        unidadeComercial: row["UNIDADE COMERCIAL"] || row["Unidade Comercial"] || row["unidadeComercial"] || undefined,
        qtdCaixaMaster: row["QTD CAIXA MASTER"] || row["Qtd Caixa Master"] || row["qtdCaixaMaster"] || undefined,
        alturaUnitaria: row["ALTURA UNITÁRIA (CM)"] || row["Altura Unitária (CM)"] || row["alturaUnitaria"] || undefined,
        larguraUnitaria: row["LARGURA UNITÁRIA (CM)"] || row["Largura Unitária (CM)"] || row["larguraUnitaria"] || undefined,
        comprimentoUnitario: row["COMPRIMENTO UNITÁRIO (CM)"] || row["Comprimento Unitário (CM)"] || row["comprimentoUnitario"] || undefined,
        unidadeFiscal: row["UNIDADE FISCAL"] || row["Unidade Fiscal"] || row["unidadeFiscal"] || undefined,
        altura: row["ALTURA CAIXA (CM)"] || row["Altura Caixa (CM)"] || row["ALTURA"] || row["Altura"] || row["altura"] || undefined,
        largura: row["LARGURA CAIXA (CM)"] || row["Largura Caixa (CM)"] || row["LARGURA"] || row["Largura"] || row["largura"] || undefined,
        comprimento: row["COMPRIMENTO CAIXA (CM)"] || row["Comprimento Caixa (CM)"] || row["COMPRIMENTO"] || row["Comprimento"] || row["comprimento"] || undefined,
        custo: row["CUSTO (R$)"] || row["Custo (R$)"] || row["custo"] || undefined,
        venda: row["VENDA (R$)"] || row["Venda (R$)"] || row["venda"] || undefined,
        eanUnit: row["EAN UNITÁRIO"] || row["EAN Unitário"] || row["eanUnit"] || undefined,
        eanCaixa: row["EAN CAIXA"] || row["EAN Caixa"] || row["eanCaixa"] || undefined,
        emitenteId: row["EMITENTE ID"] ? Number(row["EMITENTE ID"]) : undefined,
      })).filter((p: any) => p.cod);
      
      if (productsToImport.length === 0) {
        toast.error("Nenhum produto válido encontrado no arquivo");
        return;
      }
      
      await createBulkMutation.mutateAsync({ products: productsToImport });
      
      toast.success(`${productsToImport.length} produtos importados com sucesso!`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao importar produtos");
    }
    
    // Limpar input
    e.target.value = "";
  };

  const handleDownloadTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      
      // Dados de exemplo para o template
      const templateData = [
        {
          "COD": "PROD001",
          "DESCRIÇÃO": "Produto de exemplo 1",
          "NCM": "12345678",
          "UNIDADE COMERCIAL": "CX",
          "QTD CAIXA MASTER": 12,
          "ALTURA UNITÁRIA (CM)": 10,
          "LARGURA UNITÁRIA (CM)": 8,
          "COMPRIMENTO UNITÁRIO (CM)": 15,
          "UNIDADE FISCAL": "UN",
          "ALTURA CAIXA (CM)": 30,
          "LARGURA CAIXA (CM)": 20,
          "COMPRIMENTO CAIXA (CM)": 40,
          "CUSTO (R$)": 10.50,
          "VENDA (R$)": 15.00,
          "EAN UNITÁRIO": "7891234567890",
          "EAN CAIXA": "17891234567890",
          "EMITENTE ID": 1,
        },
        {
          "COD": "PROD002",
          "DESCRIÇÃO": "Produto de exemplo 2",
          "NCM": "87654321",
          "UNIDADE COMERCIAL": "UN",
          "QTD CAIXA MASTER": 24,
          "ALTURA UNITÁRIA (CM)": 5,
          "LARGURA UNITÁRIA (CM)": 4,
          "COMPRIMENTO UNITÁRIO (CM)": 8,
          "UNIDADE FISCAL": "UN",
          "ALTURA CAIXA (CM)": 15,
          "LARGURA CAIXA (CM)": 10,
          "COMPRIMENTO CAIXA (CM)": 25,
          "CUSTO (R$)": 5.25,
          "VENDA (R$)": 8.00,
          "EAN UNITÁRIO": "7899876543210",
          "EAN CAIXA": "17899876543210",
          "EMITENTE ID": 1,
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "template_produtos.xlsx");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Template baixado!");
    } catch (error) {
      toast.error("Erro ao baixar template");
    }
  };
  
  return (
    <>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Consulta de Produtos</h1>
          
          <div className="flex items-center gap-2">
            <Input
              placeholder="Buscar por COD ou Descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar Excel
            </Button>

          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Formulário de novo produto */}

        
        {/* Lista de produtos */}
        <Card>
          <CardHeader>
            <CardTitle>Produtos Cadastrados ({filteredProducts.length}{searchTerm ? ` de ${products.length}` : ""})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">ID</th>
                    <th className="text-left py-3 px-4 font-medium">COD</th>
                    <th className="text-left py-3 px-4 font-medium">Descrição</th>
                    <th className="text-right py-3 px-4 font-medium">Qtd Caixa</th>
                    <th className="text-right py-3 px-4 font-medium">Altura</th>
                    <th className="text-right py-3 px-4 font-medium">Largura</th>
                    <th className="text-right py-3 px-4 font-medium">Comprimento</th>
                    <th className="text-right py-3 px-4 font-medium">CBM</th>
                    <th className="text-right py-3 px-4 font-medium">Quantidade</th>
                    <th className="text-left py-3 px-4 font-medium">Emitente</th>
                    <th className="text-left py-3 px-4 font-medium">Localizações</th>
                    <th className="text-left py-3 px-4 font-medium">Outros Endereços</th>
                    <th className="text-center py-3 px-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="text-center py-8 text-muted-foreground">
                        Nenhum produto cadastrado
                      </td>
                    </tr>
                  ) : (
                    paginatedProducts.map((product) => {
                      const isEditing = editingId === product.id;
                      const editCbm = isEditing && editValues.altura && editValues.largura && editValues.comprimento
                        ? ((Number(editValues.altura) * Number(editValues.largura) * Number(editValues.comprimento)) / 1000000).toFixed(6)
                        : product.cbm;
                      
                      return (
                        <tr key={product.id} className="hover:bg-muted/30">
                          <td className="py-3 px-4 text-sm text-muted-foreground">{product.id}</td>
                          <td className="py-3 px-4 font-medium">{product.cod}</td>
                          <td className="py-3 px-4 text-sm">
                            {isEditing ? (
                              <Input
                                value={editValues.descricao}
                                onChange={(e) => setEditValues({ ...editValues, descricao: e.target.value })}
                                className="h-8 text-sm"
                              />
                            ) : (
                              product.descricao || "-"
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editValues.qtdCaixaMaster}
                                onChange={(e) => setEditValues({ ...editValues, qtdCaixaMaster: e.target.value })}
                                className="h-8 text-sm w-20 text-right"
                              />
                            ) : (
                              product.qtdCaixaMaster || "-"
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editValues.altura}
                                onChange={(e) => setEditValues({ ...editValues, altura: e.target.value })}
                                className="h-8 text-sm w-20 text-right"
                              />
                            ) : (
                              product.altura || "-"
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editValues.largura}
                                onChange={(e) => setEditValues({ ...editValues, largura: e.target.value })}
                                className="h-8 text-sm w-20 text-right"
                              />
                            ) : (
                              product.largura || "-"
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editValues.comprimento}
                                onChange={(e) => setEditValues({ ...editValues, comprimento: e.target.value })}
                                className="h-8 text-sm w-20 text-right"
                              />
                            ) : (
                              product.comprimento || "-"
                            )}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-sm">
                            {editCbm || "-"}
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-medium">
                            {parseInt(product.quantidade) || "0"}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {isEditing ? (
                              <Select
                                value={editValues.emitenteId?.toString() || "none"}
                                onValueChange={(value) => setEditValues({ ...editValues, emitenteId: value === "none" ? null : Number(value) })}
                              >
                                <SelectTrigger className="h-8 w-32">
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Nenhum</SelectItem>
                                  {emitentes.map((e) => (
                                    <SelectItem key={e.id} value={e.id.toString()}>
                                      {e.razaoSocial}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              emitentes.find(e => e.id === product.emitenteId)?.razaoSocial || "-"
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <ProductLocations cod={product.cod} />
                          </td>
                          <td className="py-3 px-4 text-sm">
                            <ProductExternalLocations cod={product.cod} />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedProductId(product.id);
                                setAuditDialogOpen(true);
                              }}
                              title="Ver Histórico"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Controles de paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} ({filteredProducts.length} produtos)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    
    <AuditLogDialog
      open={auditDialogOpen}
      onOpenChange={setAuditDialogOpen}
      title="Histórico de Alterações do Produto"
      logs={auditLogs}
      isLoading={auditLoading}
    />
    
    {/* Modal Criar/Editar Produto */}
    <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogMode === "create" ? "Novo Produto" : "Editar Produto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>COD *</Label>
              <Input
                value={cod}
                onChange={(e) => setCod(e.target.value)}
                placeholder="Ex: PROD-001"
                required
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descrição do produto"
                rows={1}
              />
            </div>
            
            <div>
              <Label>NCM</Label>
              <Input
                value={ncm}
                onChange={(e) => setNcm(e.target.value)}
                placeholder="Ex: 12345678"
              />
            </div>
            
            <div>
              <Label>EAN Unitário</Label>
              <Input
                value={eanUnit}
                onChange={(e) => setEanUnit(e.target.value)}
                placeholder="Ex: 7891234567890"
                maxLength={13}
              />
            </div>
            
            <div>
              <Label>EAN Caixa</Label>
              <Input
                value={eanCaixa}
                onChange={(e) => setEanCaixa(e.target.value)}
                placeholder="Ex: 17891234567897"
                maxLength={14}
              />
            </div>
            
            <div>
              <Label>Unidade Fiscal</Label>
              <Input
                value={unidadeFiscal}
                onChange={(e) => setUnidadeFiscal(e.target.value)}
                placeholder="Ex: UN"
              />
            </div>
            
            <div>
              <Label>Unidade Comercial</Label>
              <Input
                value={unidadeComercial}
                onChange={(e) => setUnidadeComercial(e.target.value)}
                placeholder="Ex: CX"
              />
            </div>
            
            <div>
              <Label>Qtd Caixa Master</Label>
              <Input
                type="number"
                value={qtdCaixaMaster}
                onChange={(e) => setQtdCaixaMaster(e.target.value)}
                placeholder="Ex: 12"
              />
            </div>
            
            <div>
              <Label>Altura Unitária (CM)</Label>
              <Input
                type="number"
                value={alturaUnitaria}
                onChange={(e) => setAlturaUnitaria(e.target.value)}
                placeholder="Ex: 10"
              />
            </div>
            
            <div>
              <Label>Largura Unitária (CM)</Label>
              <Input
                type="number"
                value={larguraUnitaria}
                onChange={(e) => setLarguraUnitaria(e.target.value)}
                placeholder="Ex: 15"
              />
            </div>
            
            <div>
              <Label>Comprimento Unitário (CM)</Label>
              <Input
                type="number"
                value={comprimentoUnitario}
                onChange={(e) => setComprimentoUnitario(e.target.value)}
                placeholder="Ex: 20"
              />
            </div>
            
            <div>
              <Label>Altura Caixa Master (CM)</Label>
              <Input
                type="number"
                value={altura}
                onChange={(e) => setAltura(e.target.value)}
                placeholder="Ex: 30"
              />
            </div>
            
            <div>
              <Label>Largura Caixa Master (CM)</Label>
              <Input
                type="number"
                value={largura}
                onChange={(e) => setLargura(e.target.value)}
                placeholder="Ex: 40"
              />
            </div>
            
            <div>
              <Label>Comprimento Caixa Master (CM)</Label>
              <Input
                type="number"
                value={comprimento}
                onChange={(e) => setComprimento(e.target.value)}
                placeholder="Ex: 50"
              />
            </div>
            
            <div>
              <Label>CBM (Calculado)</Label>
              <Input
                value={cbm}
                disabled
                className="bg-muted"
              />
            </div>
            
            <div>
              <Label>Quantidade</Label>
              <Input
                type="number"
                step="0.01"
                value={quantidade}
                readOnly
                disabled
                className="bg-muted cursor-not-allowed opacity-60"
                placeholder="Ex: 100"
              />
            </div>
            
            <div>
              <Label>Custo (R$)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  step="0.01"
                  value={custo}
                  onChange={(e) => setCusto(e.target.value)}
                  placeholder="Ex: 10.50"
                  disabled={!custoEditMode}
                  className={!custoEditMode ? "bg-muted" : ""}
                />
                {!custoEditMode && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setCustoEditMode(true)}>
                    Alterar Custo
                  </Button>
                )}
                {custoEditMode && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCustoEditMode(false)}>
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <Label>Venda (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={venda}
                onChange={(e) => setVenda(e.target.value)}
                placeholder="Ex: 15.00"
              />
            </div>
            
            <div className="col-span-2">
              <Label>Emitente</Label>
              {user?.role === "cliente" ? (
                <Input
                  value={emitentes.find((e) => e.id === emitenteId)?.razaoSocial || (emitenteId ? `Emitente #${emitenteId}` : "Carregando...")}
                  disabled
                  className="bg-muted"
                />
              ) : (
                <Select
                  value={emitenteId?.toString() || "none"}
                  onValueChange={(value) => setEmitenteId(value === "none" ? null : Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {emitentes.map((e) => (
                      <SelectItem key={e.id} value={e.id.toString()}>
                        {e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
}
